<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LhoistLogo from '@/components/LhoistLogo.vue';
import { FancyButton, ProgressBar, Button } from '@/components/ui';
import { useImageDownloader, useGlobal } from '@/composables';
import type { LanguageCode, HttpsLink } from '@/types';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 't-end'): void;
}>();

const { progress, isFinished } = useImageDownloader();
const { isLoadingScreenTransitionstartActive, isLoadingScreenTransitionendActive } = useGlobal();

const { t } = useI18n();
const { language } = useGlobal();

const isTransitionActive = ref(false);
const handleClose = () => {
  isTransitionActive.value = true;
  emit('close');
};

const el = ref<HTMLDivElement | null>(null);
const handleTransitionend = (event: TransitionEvent) => {
  if (event.target === el.value) {
    isLoadingScreenTransitionendActive.value = true;
    emit('t-end');
  }
};

const handleTransitionstart = (event: TransitionEvent) => {
  if (event.target === el.value) {
    isLoadingScreenTransitionstartActive.value = true;
  }
};
</script>

<template>
  <div
    ref="el"
    @transitionend="handleTransitionend"
    @transitionstart="handleTransitionstart"
    class="custom-loading-screen relative h-dvh overflow-hidden bg-primary"
    :class="{ 'custom-transition-active': isTransitionActive }"
  >
    <div class="custom-bg-city-image relative left-0 top-0 h-dvh w-dvw"></div>
    <div class="fixed left-0 top-0 h-dvh w-dvw">
      <div
        class="container pointer-events-auto grid size-full grid-rows-[calc(72px_+_5.25rem)_auto_3rem] justify-start md:grid-rows-[1fr_auto_1fr]"
      >
        <div class="mt-auto p-2">
          <LhoistLogo class="md:mb-16 md:mt-10" />
        </div>
        <section class="mt-auto md:my-auto">
          <div
            class="w-full space-y-6 rounded-xl bg-white p-8 md:max-w-[580px] md:rounded-br-[120px] md:p-10 md:pb-20 md:pr-20"
          >
            <h1 class="text-5xl font-bold !leading-[calc(1em_+_4px)] text-primary md:text-6xl">
              {{ t(`loading-screen.title`) }}
            </h1>
            <p class="text-shuttle-gray-600">
              {{ t(`loading-screen.description`) }}
            </p>
            <div class="flex h-12 items-center">
              <div
                v-if="isFinished"
                class="flex w-full flex-wrap items-center justify-between gap-2"
              >
                <p class="text-xl capitalize text-primary md:text-2xl">
                  {{ t(`loading-screen.discover`) }}
                </p>
                <FancyButton @click="handleClose" class="ml-auto uppercase md:ml-0">
                  {{ t(`loading-screen.enter`) }}</FancyButton
                >
              </div>
              <ProgressBar v-else :value="progress" />
            </div>
          </div>
        </section>
        <div class="mx-2 flex items-center justify-start md:mx-0">
          <Button
            variant="ghost"
            class="text-base font-normal text-white underline hover:text-white focus-visible:text-white"
            as="a"
            target="_blank"
            :href="t(`loading-screen.privacy-policy.locale.${language}.url`)"
          >
            {{ t('loading-screen.privacy-policy.label') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-loading-screen {
  opacity: var(--_opacity, 1);
  transition: opacity 0.3s 1.5s;
}

.custom-bg-city-image::before {
  content: '';

  position: absolute;
  z-index: var(--_z-index, 0);
  top: 0;
  left: 0;

  width: 100dvw;
  height: 100dvh;

  background-image: url('@/assets/images/Maps/map-rgb-quarter.avif');
  background-repeat: no-repeat;
  background-position: top-left;
  background-size: cover;

  mask: url('@/assets/images/Misc/icons/pill.svg') no-repeat;

  transition: mask 1.5s 0.5s ease-in-out;

  mask-composite: intersect;
  mask-position: right 62.5%;
  mask-size: var(--_size, 65vh);
}

.custom-transition-active {
  --_size: max(150vw, 150vh);
  /* z-index: 1; -> Allows to clip through the elements */
  --_z-index: 0;
  --_opacity: 0;
}
</style>
